import { toDegrees, toRadians } from '../geom';

export interface BuiltinFunction {
  readonly arity: number | 'variadic';
  readonly apply: (args: number[]) => number;
}

export const CONSTANTS: Readonly<Record<string, number>> = {
  pi: Math.PI,
  tau: Math.PI * 2,
  e: Math.E,
};

const unary = (apply: (x: number) => number): BuiltinFunction => ({
  arity: 1,
  apply: (args) => apply(args[0] as number),
});

const binary = (apply: (x: number, y: number) => number): BuiltinFunction => ({
  arity: 2,
  apply: (args) => apply(args[0] as number, args[1] as number),
});

export const FUNCTIONS: Readonly<Record<string, BuiltinFunction>> = {
  sin: unary((x) => Math.sin(toRadians(x))),
  cos: unary((x) => Math.cos(toRadians(x))),
  tan: unary((x) => Math.tan(toRadians(x))),
  asin: unary((x) => toDegrees(Math.asin(x))),
  acos: unary((x) => toDegrees(Math.acos(x))),
  atan: unary((x) => toDegrees(Math.atan(x))),
  atan2: binary((y, x) => toDegrees(Math.atan2(y, x))),
  rad: unary(toRadians),
  deg: unary(toDegrees),
  sqrt: unary(Math.sqrt),
  abs: unary(Math.abs),
  sign: unary(Math.sign),
  floor: unary(Math.floor),
  ceil: unary(Math.ceil),
  round: unary(Math.round),
  ln: unary(Math.log),
  log10: unary(Math.log10),
  exp: unary(Math.exp),
  pow: binary(Math.pow),
  hypot: { arity: 'variadic', apply: (args) => Math.hypot(...args) },
  min: { arity: 'variadic', apply: (args) => Math.min(...args) },
  max: { arity: 'variadic', apply: (args) => Math.max(...args) },
};
